import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaSearch } from "react-icons/fa";
import CustomerHeader from "../../components/CustomerHeader";
import CustomerFooter from "../../components/CustomerFooter";
import ServiceCard from "../../components/ServiceCard";
import ShopHero from "../../components/ShopHero";
import { formatCategoryName } from "../../utils/categoryFormatter";
import "./ServicesList.css";

/**
 * The shop's service menu at /business/:businessSlug/:locationSlug/services.
 *
 * Presentation only — the fetch, the grouping by service name (professionals may charge differently for the
 * same service) and the booking navigation are unchanged.
 *
 * Cards, not a list: the site's own language is `#dbe5ef` cards on white with a `#2d6da3` accent, and a page
 * of hairline rules read as too plain next to it. Thumbnails use `Service.serviceImage`; most shops have not
 * uploaded one, so the fallback is a designed gradient panel (the same one ShopDetails uses for its service
 * tiles) rather than a blank space or a broken image.
 */

const OTHER_GROUP = "__other";

const ServicesList = () => {
  const { businessSlug, locationSlug } = useParams();
  const navigate = useNavigate();
  const [shop, setShop] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  // The hero offers "Shop Catalogue" only when the shop actually sells products.
  const [hasProducts, setHasProducts] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    fetchServicesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessSlug, locationSlug]);

  const fetchServicesData = async () => {
    try {
      setLoading(true);

      // Fetch shop data
      const shopUrl = `${process.env.REACT_APP_API_BASE_URL}/api/business/${businessSlug}/${locationSlug}`;
      const shopRes = await fetch(shopUrl);
      if (!shopRes.ok) throw new Error("Shop location not found");
      const shopData = await shopRes.json();
      setShop(shopData);

      // Does this shop sell products? Drives the hero's "Shop Catalogue" button, as on the shop page.
      fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/v2/products/shop/${shopData._id}?featured=true&limit=1`,
      )
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          const list = data?.products || data || [];
          if (Array.isArray(list)) setHasProducts(list.length > 0);
        })
        .catch(() => {});

      // Fetch all services for this shop location
      const servicesRes = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/services?shopId=${shopData._id}`,
      );
      if (!servicesRes.ok) throw new Error("Failed to load services");

      const servicesData = await servicesRes.json();

      // Group services by name only (professionals may charge different rates)
      const serviceMap = new Map();

      (servicesData.services || []).forEach((service) => {
        const key = service.name; // Group by name only

        if (!serviceMap.has(key)) {
          // First occurrence - use this service
          serviceMap.set(key, {
            ...service,
            longestDuration: service.durationMinutes,
            availableProfessionals: [service.staffId], // Track which professionals offer this
          });
        } else {
          // Duplicate - add professional and track longest duration
          const existing = serviceMap.get(key);
          if (
            service.staffId &&
            !existing.availableProfessionals.some(
              (p) => p?._id === service.staffId?._id,
            )
          ) {
            existing.availableProfessionals.push(service.staffId);
          }
          // Track the longest duration among all professionals
          if (service.durationMinutes > existing.longestDuration) {
            existing.longestDuration = service.durationMinutes;
          }
        }
      });

      const uniqueServices = Array.from(serviceMap.values());
      setServices(uniqueServices);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleServiceSelect = (serviceName) => {
    navigate(
      `/business/${businessSlug}/${locationSlug}/book?serviceName=${encodeURIComponent(serviceName)}`,
    );
  };

  // Get unique categories
  const categories = useMemo(
    () => [...new Set(services.map((s) => s.category).filter(Boolean))],
    [services],
  );

  // Filter services
  const filteredServices = services.filter((service) => {
    const haystack = `${service.name || ""} ${
      service.description || ""
    }`.toLowerCase();
    const matchesSearch = haystack.includes(searchQuery.toLowerCase());
    const matchesCategory =
      selectedCategory === "all" || service.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Group what survived the filters, in the order it appears in the list.
  const groupedServices = useMemo(() => {
    const order = [];
    const byCategory = new Map();

    for (const service of filteredServices) {
      const key = service.category || OTHER_GROUP;
      if (!byCategory.has(key)) {
        byCategory.set(key, []);
        order.push(key);
      }
      byCategory.get(key).push(service);
    }

    return order.map((key) => ({
      key,
      label:
        key === OTHER_GROUP
          ? "Other services"
          : formatCategoryName(key) || "Services",
      items: byCategory.get(key),
    }));
  }, [filteredServices]);

  const locationLabel = [shop?.city, shop?.state].filter(Boolean).join(", ");

  if (loading) {
    return (
      <>
        <CustomerHeader />
        <div className="svc-page">
          <div className="svc-status">
            <p className="svc-status-text">Loading services…</p>
          </div>
        </div>
        <CustomerFooter />
      </>
    );
  }

  if (error || !shop) {
    return (
      <>
        <CustomerHeader />
        <div className="svc-page">
          <div className="svc-status">
            <p className="svc-status-text svc-status-error">
              {error || "Shop location not found"}
            </p>
            <button
              type="button"
              className="svc-status-button"
              onClick={() =>
                navigate(`/business/${businessSlug}/${locationSlug}`)
              }
            >
              Back to shop
            </button>
          </div>
        </div>
        <CustomerFooter />
      </>
    );
  }

  return (
    <>
      <CustomerHeader />
      <div className="svc-page">
        {/* Same hero as the shop page — the services page is a continuation of that storefront, not a
            separate screen with its own banner. */}
        <ShopHero
          name={shop.name}
          locationLine={locationLabel}
          contactLine={`📞 ${shop.phone || ""}${
            shop.email ? ` • ✉️ ${shop.email}` : ""
          }`}
          logoUrl={shop.business?.logo?.url || shop.logoUrl}
          backgroundImage={shop.images?.[0]?.url || shop.images?.[0]?.path}
          schedule={shop.schedule}
          isMobile={isMobile}
          actions={[
            {
              label: "Back to Shop",
              onClick: () =>
                navigate(`/business/${businessSlug}/${locationSlug}`),
              variant: "solid",
            },
            ...(hasProducts
              ? [
                  {
                    label: "Shop Catalogue",
                    onClick: () => navigate(`/shop/${locationSlug}/products`),
                    variant: "ghost",
                  },
                ]
              : []),
          ]}
        />

        <main className="svc-shell svc-body">
          <div className="svc-toolbar">
            <div className="svc-search">
              <span className="svc-search-icon" aria-hidden="true">
                <FaSearch size={12} />
              </span>
              <input
                type="search"
                className="svc-search-input"
                placeholder="Search services"
                aria-label="Search services"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {categories.length > 1 && (
              <div className="svc-chips">
                <button
                  type="button"
                  className="svc-chip"
                  aria-pressed={selectedCategory === "all"}
                  onClick={() => setSelectedCategory("all")}
                >
                  All
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    className="svc-chip"
                    aria-pressed={selectedCategory === cat}
                    onClick={() => setSelectedCategory(cat)}
                  >
                    {formatCategoryName(cat)}
                  </button>
                ))}
              </div>
            )}
          </div>

          {filteredServices.length === 0 ? (
            <div className="svc-empty">
              <h2 className="svc-empty-title">No services found</h2>
              <p className="svc-empty-text">
                Try a different search term or clear the category filter.
              </p>
            </div>
          ) : (
            groupedServices.map((group) => (
              <section className="svc-group" key={group.key}>
                <div className="svc-group-head">
                  <h2 className="svc-group-title">{group.label}</h2>
                  <span className="svc-group-count">
                    {group.items.length}
                  </span>
                </div>

                <ul className="service-tiles">
                  {group.items.map((service) => (
                    <li key={service._id}>
                      <ServiceCard
                        service={service}
                        professionalCount={
                          service.availableProfessionals?.length || 0
                        }
                        onSelect={() => handleServiceSelect(service.name)}
                      />
                    </li>
                  ))}
                </ul>
              </section>
            ))
          )}

          {filteredServices.length > 0 && (
            <p className="svc-count">
              Showing {filteredServices.length} of {services.length} services
            </p>
          )}
        </main>
      </div>
      <CustomerFooter />
    </>
  );
};

export default ServicesList;
